import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { CurrencyBtc, TrendUp } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/trade");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen w-screen flex bg-[#0A0E17]">
      {/* Left visual */}
      <div className="hidden md:flex w-1/2 relative overflow-hidden border-r border-[#1F2937]">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1760978631985-590e3b5f4057?crop=entropy&cs=srgb&fm=jpg&q=85')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0E17]/95 via-[#0A0E17]/70 to-[#0A0E17]" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-[#FCD535] flex items-center justify-center rounded-sm">
              <CurrencyBtc size={22} weight="bold" className="text-black" />
            </div>
          </div>
          <div className="space-y-6">
            <p className="overline">Investment arena</p>
            <h1 className="text-5xl font-bold leading-tight">
              Trade crypto.<br />
              <span className="text-[#FCD535]">Risk nothing.</span>
            </h1>
            <p className="text-[#9CA3AF] text-base max-w-md">
              Real-time market data, leverage up to 100x, and support from top traders.
            </p>
            <div className="flex items-center gap-2 text-sm text-[#0ECB81]">
              <TrendUp size={18} weight="bold" />
              <span>Live BTC/ETH/SOL/+40 pairs</span>
            </div>
          </div>
          <div className="text-xs text-[#9CA3AF]">© 2015 Investment arena</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div className="space-y-2">
            <p className="overline">Authentication</p>
            <h2 className="text-3xl font-semibold">Sign in</h2>
            <p className="text-sm text-[#9CA3AF]">Access your training portfolio</p>
          </div>
          <div className="space-y-3">
            <div>
              <Label htmlFor="email" className="overline mb-1.5 block">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-[#0A0E17] border-[#1F2937] focus:border-[#FCD535] h-11 rounded-sm"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="overline mb-1.5 block">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-[#0A0E17] border-[#1F2937] focus:border-[#FCD535] h-11 rounded-sm"
                data-testid="login-password-input"
              />
            </div>
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full h-11 bg-[#FCD535] hover:bg-[#FCD535]/90 text-black font-semibold rounded-sm"
            data-testid="login-submit-button"
          >
            {loading ? "Signing in..." : "Sign in"}
          </Button>
          <p className="text-sm text-center text-[#9CA3AF]">
            New here?{" "}
            <Link to="/register" className="text-[#FCD535] hover:underline" data-testid="register-link">
              Create account
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
